import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Megaphone, Loader2, Save, CheckCircle2, AlertTriangle, ChevronDown,
  KeyRound, Activity, Link2, Unplug,
} from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { useTheme } from '../../contexts/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

/** Collapsible setup panel with a down-caret header — matches YouTubeScraperCard. */
function Section({ title, subtitle, icon: Icon, defaultOpen = false, testid, isDark, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={`pt-3 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`} data-testid={testid}>
      <CollapsibleTrigger className="flex w-full items-center justify-between text-left" data-testid={testid ? `${testid}-toggle` : undefined}>
        <span className="flex items-center gap-2">
          {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
          <span className={`text-[11px] font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{title}</span>
          {subtitle && <span className="text-[10px] text-slate-500">· {subtitle}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 pt-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

/**
 * FacebookCard — Tools → Facebook.
 * Connects the org's Facebook Page so a News story can be published to it.
 * Nothing is hardcoded: App ID / App Secret / Page token live in Mongo
 * `meta_api_config` (services/meta_oauth). Swapping the Page or the Meta app
 * later is a UI-only operation (Disconnect → edit → Connect).
 *
 * No App Review is involved: posting to a Page the connecting user
 * administers runs on Meta's Standard Access.
 */
export default function FacebookCard({ orgId }) {
  const { isDark } = useTheme();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ app_id: '', app_secret: '', expected_page_id: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data } = await axios.get(`${API}/api/facebook/${orgId}/config`, { withCredentials: true });
      setCfg(data || {});
      setForm({
        app_id: data?.app_id || '',
        app_secret: '',                       // never echoed back by the API
        expected_page_id: data?.expected_page_id || '',
      });
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Could not load Facebook config.' });
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const payload = { app_id: form.app_id, expected_page_id: form.expected_page_id };
      if (form.app_secret.trim()) payload.app_secret = form.app_secret;   // blank = keep current
      const { data } = await axios.put(`${API}/api/facebook/${orgId}/config`, payload, { withCredentials: true });
      setCfg(data);
      setForm((f) => ({ ...f, app_secret: '' }));
      setMsg({ kind: 'ok', text: 'Saved.' });
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Save failed.' });
    } finally { setBusy(false); }
  };

  const connect = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await axios.post(`${API}/api/facebook/${orgId}/oauth/start`, {}, { withCredentials: true });
      if (data?.auth_url) {
        window.location.href = data.auth_url;
        return;                       // navigating away; leave the spinner on
      }
      // 200 with no auth_url: don't strand the card on a spinner forever.
      setMsg({ kind: 'error', text: 'Facebook did not return a sign-in URL. Check the App ID and Secret.' });
      setBusy(false);
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Could not start the Facebook connection.' });
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await axios.post(`${API}/api/facebook/${orgId}/test`, {}, { withCredentials: true });
      setMsg(data.ok
        ? { kind: 'ok', text: `Reached “${data.page_name}”${data.followers != null ? ` · ${data.followers} followers` : ''}.` }
        : { kind: 'error', text: data.error || 'Test failed.' });
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Test failed.' });
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect this Facebook Page?\n\nAlready-published posts stay on Facebook. Nothing on the website changes.')) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await axios.post(`${API}/api/facebook/${orgId}/disconnect`, {}, { withCredentials: true });
      setCfg(data);
      setMsg({ kind: 'ok', text: 'Disconnected.' });
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Disconnect failed.' });
    } finally { setBusy(false); }
  };

  const connected = !!cfg?.connected;
  // The dashboard can be light or dark; slate-400 labels on a light card are
  // invisible (caught on PROD 2026-10-11), so every token is theme-aware.
  const inputCls = isDark
    ? 'bg-slate-900 border-slate-700 text-slate-100 text-xs h-8'
    : 'bg-white border-slate-300 text-slate-900 text-xs h-8';
  const cardCls = isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white';
  const headCls = isDark ? 'text-slate-100' : 'text-slate-900';
  const bodyCls = isDark ? 'text-slate-400' : 'text-slate-600';
  const labelCls = isDark ? 'text-slate-400' : 'text-slate-700';
  const hintCls = isDark ? 'text-slate-500' : 'text-slate-500';

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${cardCls}`} data-testid="facebook-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Megaphone className="h-4 w-4 text-blue-400" />
        <h3 className={`text-sm font-bold ${headCls}`}>Facebook Page</h3>
        {connected
          ? <Badge className="bg-emerald-600 text-[10px]" data-testid="fb-connected-badge">
              Connected: {cfg.page_name || cfg.page_id}
            </Badge>
          : <Badge className="bg-slate-600 text-[10px]" data-testid="fb-disconnected-badge">Not connected</Badge>}
      </div>

      <p className={`text-[11px] leading-relaxed ${bodyCls}`}>
        Lets a News story be published to the club’s Facebook Page. Choose the destinations
        when you write the story — WordPress, Facebook, or both.
        {connected && cfg.connected_at && (
          <> Connected {new Date(cfg.connected_at).toLocaleString()}.</>
        )}
      </p>

      {msg && (
        <div className={`flex items-start gap-2 text-[11px] rounded border p-2 ${
          msg.kind === 'ok'
            ? 'border-emerald-800 bg-emerald-900/20 text-emerald-300'
            : 'border-amber-800 bg-amber-900/20 text-amber-300'}`}>
          {msg.kind === 'ok' ? <CheckCircle2 className="h-3.5 w-3.5 mt-px shrink-0" />
                             : <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={connect} disabled={busy || !cfg?.app_secret_set}
                className="h-7 text-[11px]" data-testid="fb-connect-btn">
          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Link2 className="h-3 w-3 mr-1" />}
          {connected ? 'Reconnect' : 'Connect Facebook'}
        </Button>
        <Button size="sm" variant="outline" onClick={test} disabled={busy || !connected}
                className="h-7 text-[11px]" data-testid="fb-test-btn">Test connection</Button>
        <Button size="sm" variant="outline" onClick={disconnect} disabled={busy || !connected}
                className="h-7 text-[11px] text-amber-300" data-testid="fb-disconnect-btn">
          <Unplug className="h-3 w-3 mr-1" />Disconnect
        </Button>
      </div>
      {!cfg?.app_secret_set && (
        <p className={`text-[10px] ${hintCls}`}>Save an App ID and App Secret below before connecting.</p>
      )}

      <Section title="Connection & credentials" subtitle="rarely changes once set up"
               icon={KeyRound} testid="fb-creds" defaultOpen={!connected} isDark={isDark}>
        <div>
          <label className={`text-[10px] ${labelCls}`}>Meta App ID</label>
          <Input value={form.app_id} onChange={(e) => setForm((f) => ({ ...f, app_id: e.target.value }))}
                 placeholder="App ID — Meta → App settings → Basic" className={inputCls} data-testid="fb-app-id" />
        </div>
        <div>
          <label className={`text-[10px] ${labelCls}`}>Meta App Secret</label>
          <Input type="password" value={form.app_secret}
                 onChange={(e) => setForm((f) => ({ ...f, app_secret: e.target.value }))}
                 placeholder={cfg?.app_secret_set ? '••• saved — leave blank to keep' : 'App Secret — Meta → App settings → Basic → Show'}
                 className={inputCls} data-testid="fb-app-secret" />
        </div>
        <div>
          <label className={`text-[10px] ${labelCls}`}>Expected Page ID</label>
          <Input value={form.expected_page_id}
                 onChange={(e) => setForm((f) => ({ ...f, expected_page_id: e.target.value }))}
                 placeholder="leave blank for the first connect" className={inputCls}
                 data-testid="fb-expected-page" />
          <p className={`text-[10px] mt-1 ${hintCls}`}>
            A safety guard. When set, connecting with the wrong Page is refused and no token is
            stored. Leave it blank the first time — the connect screen lists every Page ID it
            covered, then paste the right one here.
          </p>
        </div>
        <Button size="sm" onClick={save} disabled={busy} className="h-7 text-[11px]" data-testid="fb-save-btn">
          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save
        </Button>
      </Section>

      <Section title="Recent activity" subtitle="connects and published posts"
               icon={Activity} testid="fb-activity" isDark={isDark}>
        {(cfg?.run_log || []).length === 0
          ? <p className={`text-[11px] ${hintCls}`}>Nothing yet.</p>
          : [...(cfg.run_log || [])].reverse().map((r, i) => (
              <div key={i} className="flex items-start gap-2 text-[10px]">
                {r.ok ? <CheckCircle2 className="h-3 w-3 text-emerald-400 mt-px shrink-0" />
                      : <AlertTriangle className="h-3 w-3 text-amber-400 mt-px shrink-0" />}
                <span className="text-slate-500">{new Date(r.at).toLocaleString()}</span>
                <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{r.summary || r.action}</span>
              </div>
            ))}
      </Section>
    </div>
  );
}
